import React, { useRef, useState } from 'react';
import AlignedImageBlock from '@blocks/aligned-image-block/aligned-image-block';
import AutomatedListingTileBlock from '@blocks/automated-listing-tile-block/automated-listing-tile-block';
import EmbedBlock from '@blocks/embed-block/embed-block';
import ImageBlock from '@blocks/image-block/image-block';
import LogoBlock from '@blocks/logo-block/logo-block';
import SingleCtaBlock from '@blocks/single-cta-block/single-cta-block';
import TileBlock from '@blocks/tile-block/tile-block';
import TabTitles from './tab-titles';
import styled from '@emotion/styled/macro';
import useScrollArrows from '@hooks/use-scroll-arrows';
import vars from '@variables';
import {
  LeftScrollArrow,
  RightScrollArrow,
} from '@components/scroll-arrows/scroll-arrows';
import { defineResponsiveGridColumns } from '@utils/utils';
import { sectionTitleBorder } from '../../mixins';
import { TabbedBlockProps } from './tabbed-block.types';
import { H2Atom, HighlightAtom, PAtom } from '@atoms/text-atoms/text-atoms';
import { GTM_ID } from '../../../../common/constants/gtmIds';

const {
  transformSingleCtaBlockContent,
  transformTileBlockContent,
  transformAutomatedTileListingBlock,
  transformLogoBlock,
  transformAlignedImageBlock,
} = require('../../../../utils/transform-data-functions');

const TabbedBlock: React.FC<TabbedBlockProps> = ({
  localeCode,
  anchorId,
  title,
  description = '',
  tabs,
  colour = vars.coreDarkGrey,
  className,
  staticTranslations,
  currentEntryId,
}) => {
  const transformTabContent = (type, content) => {
    switch (type) {
      case 'ContentfulAutomatedListingTileBlock':
        return transformAutomatedTileListingBlock(
          content,
          localeCode,
          staticTranslations,
          colour,
          currentEntryId
        );
      case 'ContentfulLogoBlock':
        return transformLogoBlock({
          block: content,
          pageColour: colour,
          localeCode,
        });
      case 'ContentfulSingleCtaBlock':
        return transformSingleCtaBlockContent(content, localeCode);
      case 'ContentfulTileBlock':
        return transformTileBlockContent(content, localeCode);
      case 'ContentfulImageBlock':
        return content;
      case 'ContentfulAlignedImageBlock':
        return transformAlignedImageBlock({ block: content, localeCode });
      default:
        return content;
    }
  };

  const mappedTabs = tabs.map(tab => {
    return {
      id: tab.id,
      tabTitle: tab.tabTitle,
      type: tab.tabContent.internal.type,
      tabContent: transformTabContent(
        tab.tabContent.internal.type,
        tab.tabContent
      ),
      localeCode,
      staticTranslations,
      colour,
      currentEntryId,
    };
  });

  const [activeTab, setActiveTab] = useState(0);

  const handleTabClick = index => {
    setActiveTab(index);
  };

  const tabsRef = useRef<HTMLUListElement>(null);
  const { showLeftArrow, showRightArrow, scrollTabs } = useScrollArrows({
    tabsRef,
  });

  if (tabs.length > 0) {
    return (
      <div id={anchorId} className={className} data-testid="tabbed-block">
        <div className="component-grid-structure">
          <H2Atom className="block-title">{title}</H2Atom>
          {description && (
            <PAtom className="block-subtitle">{description}</PAtom>
          )}

          <div className="tabbed-block-tabs-container">
            {showLeftArrow && (
              <LeftScrollArrow
                showLeftArrow={showLeftArrow}
                scrollTabs={scrollTabs}
              />
            )}

            <TabTitles
              tabs={mappedTabs}
              activeTab={activeTab}
              onTabClick={handleTabClick}
              parentRef={tabsRef}
              gtmId={GTM_ID.TABBED_BLOCK_TAB}
            />

            {showRightArrow && (
              <RightScrollArrow
                showRightArrow={showRightArrow}
                scrollTabs={scrollTabs}
              />
            )}
          </div>

          <div className="tabs-separator" />

          <div className="tab-content">
            {mappedTabs.map((tab, index) => (
              <div key={tab.id} className="tabWrapper">
                {index === activeTab && (
                  <>
                    {tab.type === 'ContentfulAlignedImageBlock' && (
                      <AlignedImageBlock
                        {...tab.tabContent}
                        imageSrc={tab.tabContent.imageSrc}
                        gatsbyImageData={tab.tabContent.gatsbyImageData}
                        imageLink={tab.tabContent.imageLink}
                        imageOpenInNewTab={tab.tabContent.imageOpenInNewTab}
                        internalImageLink={tab.tabContent.internalImageLink}
                        mediaUrl={tab.tabContent.mediaUrl}
                        firstButtonText={tab.tabContent.firstButtonText}
                        firstButtonLink={tab.tabContent.firstButtonLink}
                        secondButtonText={tab.tabContent.secondButtonText}
                        secondButtonLink={tab.tabContent.secondButtonLink}
                      />
                    )}
                    {tab.type === 'ContentfulEmbedBlock' && (
                      <EmbedBlock
                        {...tab.tabContent}
                        embedId={tab.tabContent.id}
                        displayTitle={tab.tabContent.title}
                        description={tab.tabContent.shortDescription}
                        url={tab.tabContent.embedUrl.embedUrl}
                      />
                    )}
                    {tab.type === 'ContentfulImageBlock' && (
                      <ImageBlock {...tab.tabContent} />
                    )}
                    {tab.type === 'ContentfulLogoBlock' && (
                      <LogoBlock {...tab.tabContent} />
                    )}
                    {tab.type === 'ContentfulAutomatedListingTileBlock' && (
                      <AutomatedListingTileBlock {...tab.tabContent} />
                    )}
                    {tab.type === 'ContentfulSingleCtaBlock' && (
                      <SingleCtaBlock
                        {...tab.tabContent}
                        content={tab.tabContent.content?.raw}
                      />
                    )}
                    {tab.type === 'ContentfulTileBlock' && (
                      <TileBlock
                        {...tab.tabContent}
                        description={tab.tabContent.descriptionShort}
                      />
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  } else {
    console.error('Tabbed Block called with missing tabs!');
    return null;
  }
};

const StyledTabbedBlock = styled(TabbedBlock)`
  width: 100%;
  margin-top: ${vars.spacingXxl};
  margin-bottom: ${vars.spacingXxl};
  .component-grid-structure {
    display: grid;
    column-gap: ${vars.mobileColumnPercentageGap};
    grid-template-columns: ${defineResponsiveGridColumns(4)};
    @media screen and (min-width: ${vars.breakPointTablet}) {
      column-gap: ${vars.standardColumnPercentageGap};
      grid-template-columns: ${defineResponsiveGridColumns(12)};
    }
  }
  ${H2Atom} {
    font-weight: ${vars.fontWeightBold};
    grid-column: col1-start / col7-end;
    ${({ colour }) => sectionTitleBorder(colour)};
    margin-bottom: ${vars.spacingM};
  }
  .block-subtitle {
    grid-column: col1-start / col7-end;
    margin-bottom: ${vars.spacingM};
  }

  ${HighlightAtom} {
    ${vars.fontSize7}
    line-height: 1.33;
  }
  .tabbed-block-tabs-container {
    display: flex;
    grid-column: col1-start / col4-end;
    @media screen and (min-width: ${vars.breakPointTablet}) {
      grid-column: col1-start / col12-end;
    }
    position: relative;
  }
  .tabs-separator {
    display: block;
    grid-column: col1-start / col4-end;
    border-top: 0px;
    margin-bottom: 3rem;
    margin-top: -1px;
    @media screen and (min-width: ${vars.breakPointTablet}) {
      grid-column: col1-start / col12-end;
    }
  }

  .tab-content {
    grid-column: col1-start / col4-end;
    @media screen and (min-width: ${vars.breakPointTablet}) {
      grid-column: col1-start / col12-end;
    }

    .tabWrapper {
      display: block;
      width: 100%;
      ${PAtom} {
        line-height: 1.6;
      }

      ${LogoBlock} {
        h2 {
          grid-column: col1-start / col12-end;
        }
        .logos-container {
          flex-wrap: nowrap;
        }
      }
    }
  }
`;

export default StyledTabbedBlock;
